import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { WidgetCanvas } from '@t-one-internal/design-system';
import { publicDashboardService } from '../api/publicApiClient';
import { usePublicAnalyticsData } from '../hooks/usePublicAnalyticsData';
import { WidgetWrapper } from '../components/widgets/WidgetWrapper';
import { DrillToDetailModal } from '../components/widgets/DrillToDetailModal';
import type { PublicDashboardResponse, DashboardWidget, GridLayoutItem } from '../types/widget';
import type { QueryFilter } from '../types/analytics';
import type { DrillToDetailContext } from '../types/drillToDetail';

// Simple loading spinner
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

// Error display component
const ErrorDisplay: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div className="min-h-screen bg-white flex items-center justify-center p-6">
    <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-500 mb-6">{message}</p>
      <Link
        to="/login"
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
      >
        Sign in to Trainery
      </Link>
    </div>
  </div>
);

/**
 * Public widget that fetches data via public API and renders through WidgetWrapper.
 * This gives us the full widget chrome (title, expand, drill-to-detail) without auth.
 */
const PublicWidget: React.FC<{
  widget: DashboardWidget;
  ownerUserId: string;
  accountId: string;
  shareToken: string;
  onDrillToDetail?: (context: DrillToDetailContext) => void;
}> = ({ widget, ownerUserId, accountId, shareToken, onDrillToDetail }) => {
  const queryConfig = widget.query_config as any;
  const visualizationConfig = widget.visualization_config as any;
  const metricKey = queryConfig?.metric_key;
  const chartType = visualizationConfig?.chart_type;
  const scoping = ((widget as any).scoping || queryConfig?.scoping) as string | undefined;

  const publicData = usePublicAnalyticsData({
    queryConfig: widget.query_config,
    ownerUserId,
    accountId,
    shareToken,
    scoping,
  });

  return (
    <WidgetWrapper
      widgetId={widget.id}
      title={widget.title}
      templateKey={widget.template_key}
      metricKey={metricKey}
      queryConfig={widget.query_config}
      visualizationConfig={widget.visualization_config}
      chartType={chartType}
      filters={(widget.filters as QueryFilter[]) || []}
      capabilities={widget.capabilities as any}
      currentChartType={chartType}
      onDrillToDetail={onDrillToDetail}
      ownerUserId={ownerUserId}
      readOnly={true}
      scoping={scoping}
      showFilterIcon={false}
      externalData={publicData}
      publicContext={{ ownerUserId, accountId, shareToken }}
    />
  );
};

/**
 * Public Dashboard Page
 *
 * Renders a shared dashboard for unauthenticated users.
 * Uses WidgetWrapper with externalData for full widget chrome
 * (titles, cards, expand, drill-to-detail) without requiring auth.
 */
const PublicDashboardPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [dashboard, setDashboard] = useState<PublicDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  // Drill-to-detail state
  const [drillToDetailContext, setDrillToDetailContext] = useState<DrillToDetailContext | null>(null);

  const handleDrillToDetail = useCallback((context: DrillToDetailContext) => {
    setDrillToDetailContext(context);
  }, []);

  // Load the shared dashboard
  useEffect(() => {
    if (!token) {
      setError({
        title: 'Invalid Link',
        message: 'This share link is missing the required token.',
      });
      setLoading(false);
      return;
    }

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await publicDashboardService.getSharedDashboard(token);
        setDashboard(data);
      } catch (err: any) {
        console.error('Failed to load public dashboard:', err);

        if (err.response?.status === 404) {
          setError({
            title: 'Link Not Found',
            message: 'This share link is invalid or has been revoked.',
          });
        } else if (err.response?.status === 410) {
          setError({
            title: 'Link Expired',
            message: 'This share link has expired and is no longer accessible.',
          });
        } else {
          setError({
            title: 'Unable to Load',
            message: 'Unable to load this dashboard. Please try again later.',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [token]);

  // Convert widgets to canvas format
  const convertToCanvasWidget = useCallback((widget: DashboardWidget) => {
    const visualizationConfig = widget.visualization_config as any;
    const layoutConstraints = visualizationConfig?.layout;

    return {
      id: String(widget.id),
      type: visualizationConfig?.chart_type || 'custom',
      title: widget.title,
      layout: {
        i: String(widget.id),
        x: widget.position_x,
        y: widget.position_y,
        w: widget.width,
        h: widget.height,
        minW: layoutConstraints?.min_width ?? 3,
        minH: layoutConstraints?.min_height ?? 3,
        maxW: layoutConstraints?.max_width ?? 12,
        maxH: layoutConstraints?.max_height ?? 8,
        static: true,
      } as GridLayoutItem,
      data: widget.query_config,
      config: widget.visualization_config,
    };
  }, []);

  // Render widget content using WidgetWrapper
  const renderWidgetContent = useCallback(
    (widget: { id: string; title: string; data: unknown; config: unknown }) => {
      if (!dashboard || !token) return null;

      const dashboardWidget = dashboard.widgets.find((w) => String(w.id) === widget.id);
      if (!dashboardWidget) return null;

      return (
        <PublicWidget
          widget={dashboardWidget}
          ownerUserId={dashboard.owner.user_id}
          accountId={dashboard.owner.account_id}
          shareToken={token}
          onDrillToDetail={handleDrillToDetail}
        />
      );
    },
    [dashboard, token, handleDrillToDetail]
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
          </div>
        </header>
        <main className="max-w-7xl mx-auto p-6">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return <ErrorDisplay title={error.title} message={error.message} />;
  }

  // Dashboard not found
  if (!dashboard) {
    return <ErrorDisplay title="Dashboard Not Found" message="This dashboard could not be loaded." />;
  }

  const canvasWidgets = dashboard.widgets.map(convertToCanvasWidget);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">T</span>
            </div>
            <span className="text-xl font-semibold text-gray-900">Trainery</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
              Shared Dashboard
            </span>
            <Link
              to="/login"
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* Dashboard Title */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">{dashboard.dashboard.name}</h1>
          {dashboard.dashboard.description && (
            <p className="text-gray-500 mt-1">{dashboard.dashboard.description}</p>
          )}
        </div>
      </div>

      {/* Widget Canvas */}
      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto">
          {canvasWidgets.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <p className="text-gray-500">This dashboard has no widgets.</p>
            </div>
          ) : (
            <WidgetCanvas
              widgets={canvasWidgets}
              onLayoutChange={() => {}}
              onRemoveWidget={() => {}}
              isDraggable={false}
              isResizable={false}
              renderWidget={renderWidgetContent}
            />
          )}
        </div>
      </main>

      {/* Drill-to-Detail Modal */}
      {drillToDetailContext && (
        <DrillToDetailModal
          isOpen={true}
          onClose={() => setDrillToDetailContext(null)}
          context={drillToDetailContext}
        />
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-gray-500">
          <span>Powered by Trainery</span>
          <div className="flex items-center gap-4">
            <a href="https://trainery.com" className="hover:text-gray-700 transition-colors">
              Learn More
            </a>
            <Link to="/register" className="hover:text-gray-700 transition-colors">
              Sign Up
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicDashboardPage;
